import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import { useFormikContext } from 'formik';
import { Grid } from '@material-ui/core';
import { FarmRiskType, INLAND_MARINE_RISK_NAME, lookupsJson, policyType } from '@ourbranch/lookups';
import { FARM_LIABILITY_RISK_SELECTION, isLiabilityOnlyRiskSelection } from '@ourbranch/farm-utils';

import Field from 'core/components/form/form.v2';
import withDatePicker from 'core/components/with-date-picker';
import useSession from 'core/hooks/use-session';
import {
  getMaskedRiskTypeOptions,
  isLiabilityOnlyQuotingEnabled,
  resetFarmRiskBuildingOnTypeChange,
  clearFarmRiskBuildingAndCoverages
} from 'common/helpers/farm-helpers';
import { InlandMarineForm } from 'common/components/farm/locations/risks/sub-risks';

import FO3RiskDetail from './FO3-risk-detail';
import FO4RiskDetail from './FO4-risk-detail';
import FO6RiskDetail from './FO6-risk-detail';
import FO6ScheduledRiskDetail from './FO6-scheduled-risk-detail';
import useStyles from './initial-risk-detail.styles';

const baseRiskTypeOptions = lookupsJson.farm.riskType;

// `missingDataCodes`: forwarded to the dwelling forms so a caller fixing a known rejection (the offer
// sidebar's 50006 button) gets the missing-data fields without a new-quote store alert (#36621).
const InitialRiskDetail = ({ missingDataCodes = [] }) => {
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext();
  const session = useSession();

  // Extract frequently accessed nested values to improve readability
  const farmLocation = values.farmLocations?.[0];
  const farmRisk = farmLocation?.risks?.[0];
  const farmLocationPath = 'farmLocations[0]';
  const farmRiskPath = `${farmLocationPath}.risks[0]`;

  // Filter+relabel risk type options by policy type mask. For Forreston Rental (Y), drops
  // FO-4 / Scheduled / Unscheduled Farm Property and relabels FO-6 → "Other Structures (FO-6)". (#31367)
  // Resolve the mask from the primary LOCATION's state, not the applicant/mailing state — an
  // out-of-state mailing address otherwise finds no mask and leaks the farm-only risk types onto
  // Home/Rental (#32094). Fall back to the mailing state before a location address is entered.
  const riskMaskState = farmLocation?.address?.state || values.state;
  let riskTypeOptions = getMaskedRiskTypeOptions(values.policyType, riskMaskState, baseRiskTypeOptions);

  // #33164: standalone inland marine is underwriter-only to START. The offer/policy routes enforce
  // the same rule from the other end (`applyInlandMarineExclusivity` in common/helpers/farm-helpers),
  // so the two together mean no agent can ever put the first inland marine risk on an offer. They are
  // deliberately not one function: this form always starts from a single risk, so it needs the
  // already-selected escape and never the exclusivity cases; the offer route needs both and has no
  // pre-selected type. Widen the role gate in one and check the other.
  // The already-selected escape is not cosmetic: `resubmit-offer-modal` reuses this component on a
  // persisted offer, and the effect below blanks a `type` that is no longer an option — so without
  // it a non-underwriter opening that modal on an inland marine offer would wipe the risk.
  if (!session.canApproveUWReview && farmRisk?.type !== FarmRiskType.InlandMarine) {
    riskTypeOptions = riskTypeOptions.filter((opt) => opt.id !== FarmRiskType.InlandMarine);
  }

  // Liability-only quoting (#33168, extended to Home by #34379): "Liability" is a form-only sentinel
  // appended after the real risk types (full Farm F and Home Z, brand-flag gated) — never added to
  // the lookups riskType list or the mask, since it is not a FarmRiskType. Selecting it submits the
  // location with NO risks and includeFarmLiability on (translated in quoter.service), returning a
  // zero-risk offer whose liability block is toggled on. In the UI it behaves like FO6 unscheduled:
  // auto-named, no sub-detail form. Rental (Y) is intentionally excluded (Homeowners-only ask).
  const supportsLiabilityOnlyQuoting =
    values.policyType === policyType.Farm || values.policyType === policyType.FarmHome;
  if (supportsLiabilityOnlyQuoting && isLiabilityOnlyQuotingEnabled()) {
    riskTypeOptions = [...riskTypeOptions, { id: FARM_LIABILITY_RISK_SELECTION, value: 'Liability' }];
  }

  // Clear the selected risk type only when it's no longer one of the currently offered options.
  // The options depend on BOTH the policy type (#32225) and the state-specific mask (#32094), so the
  // effect intentionally re-runs on either `values.policyType` or `riskMaskState`. The guard
  // (`!riskTypeOptions.some(...)`) means a still-valid selection is never wiped — only a selection
  // that the new policy type OR the new location state has made invalid is cleared. (#32273)
  useEffect(() => {
    if (farmRisk?.type && riskTypeOptions.length > 0 && !riskTypeOptions.some((opt) => opt.id === farmRisk.type)) {
      setFieldValue(`${farmRiskPath}.type`, '');
      setFieldValue(`${farmRiskPath}.description`, '');
      // #32514 reopen: also drop the now-orphaned building/coverages. This effect blanks `type` to
      // '' before the user re-picks, so the onChange cross-family reset can no longer resolve a
      // `fromType` family on the next selection — leaving the previous type's fields (e.g. an FO-6
      // coverageF) to flow into the replacement type's rater and reproduce the "farm rating error".
      clearFarmRiskBuildingAndCoverages({ riskPath: farmRiskPath, setFieldValue });
    }
  }, [values.policyType, riskMaskState]);

  return (
    <Grid container spacing={2}>
      <Field
        xs={6}
        type="select"
        id="farmRiskType"
        name={`${farmRiskPath}.type`}
        mode="light"
        label="Risk Type*"
        options={riskTypeOptions}
        fast={false}
        onChange={(value) => {
          // #32514 reopen: switching the primary structure across form families (e.g. FO-6 "other
          // structures" → FO-3 dwelling) leaves the previous type's building + coverages in place.
          // Those incompatible fields flow into the dwelling rater and fail with a "farm rating
          // error" (an agent hit this after entering an FO-6, getting the missing-dwelling error,
          // then switching to FO-3). Reset the stale data on any cross-family switch so the new
          // type's form re-collects the right details. Same-family switches (FO-1 ↔ FO-3) are a no-op.
          resetFarmRiskBuildingOnTypeChange({
            fromType: farmRisk?.type,
            toType: value,
            riskPath: farmRiskPath,
            setFieldValue
          });
          // #33164: clear explicitly whenever standalone inland marine is on either side of the switch
          // — it has no `building` at all, and its SPP items must not ride along into a dwelling quote.
          // The generic cross-family reset above already covers this, since `FarmRiskFamily.InlandMarine`
          // exists and `isCrossFamilyFarmRiskChange` only bails on a null family. Kept as a deliberate
          // belt-and-braces: this reset is the difference between a working quote and the "farm rating
          // error" of #32514, and a building-less risk is the case where stale data is least visible.
          if (value === FarmRiskType.InlandMarine || farmRisk?.type === FarmRiskType.InlandMarine) {
            clearFarmRiskBuildingAndCoverages({ riskPath: farmRiskPath, setFieldValue });
          }
          if (value === FarmRiskType.Fo6Scheduled) {
            setFieldValue(`${farmRiskPath}.description`, 'FO6 Scheduled');
          } else if (value === FarmRiskType.Fo6Unscheduled) {
            setFieldValue(`${farmRiskPath}.description`, 'FO6 Unscheduled');
          } else if (value === FarmRiskType.InlandMarine) {
            // Auto-named like the FO6 types — the risk insures scheduled items, not a described
            // structure, so there is nothing for the agent to name. (#33164)
            setFieldValue(`${farmRiskPath}.description`, INLAND_MARINE_RISK_NAME);
          } else if (isLiabilityOnlyRiskSelection(value)) {
            // Liability sentinel (#33168): auto-name like the FO6 types, and explicitly drop any
            // building/coverages — the sentinel has no risk family, so the cross-family reset
            // above never fires for it, and stale dwelling data must not ride along in form state.
            setFieldValue(`${farmRiskPath}.description`, 'Liability');
            clearFarmRiskBuildingAndCoverages({ riskPath: farmRiskPath, setFieldValue });
          } else if (
            farmRisk?.type === FarmRiskType.Fo6Scheduled ||
            farmRisk?.type === FarmRiskType.Fo6Unscheduled ||
            farmRisk?.type === FarmRiskType.InlandMarine ||
            isLiabilityOnlyRiskSelection(farmRisk?.type)
          ) {
            // Clear description when switching FROM auto-description types to user-editable types
            setFieldValue(`${farmRiskPath}.description`, '');
          }
        }}
      />
      <Field
        xs={6}
        type="string"
        id="farmRisk.description"
        name={`${farmRiskPath}.description`}
        mode="light"
        label="Risk Name"
        disabled={
          farmRisk?.type === FarmRiskType.Fo6Scheduled ||
          farmRisk?.type === FarmRiskType.Fo6Unscheduled ||
          farmRisk?.type === FarmRiskType.InlandMarine ||
          isLiabilityOnlyRiskSelection(farmRisk?.type)
        }
      />

      {[FarmRiskType.Fo1, FarmRiskType.Fo2, FarmRiskType.Fo3].includes(farmRisk?.type) && (
        <FO3RiskDetail missingDataCodes={missingDataCodes} />
      )}

      {farmRisk?.type === FarmRiskType.Fo4 && <FO4RiskDetail missingDataCodes={missingDataCodes} />}

      {farmRisk?.type === FarmRiskType.Fo6Structures && <FO6RiskDetail />}

      {/* #34585: an FO-6 scheduled risk has no building and no limit of its own — its Coverage F
          items ARE the risk. A farm offer's total premium can never be $0, so an item-less one is
          blocked by ClarionDoor ("did not receive premium" → BLOCK 5042) on the first "See price",
          not merely quoted cheap. Same reason the inland marine form below sits here. */}
      {farmRisk?.type === FarmRiskType.Fo6Scheduled && <FO6ScheduledRiskDetail />}

      {/* #33164: a standalone inland marine risk has no building to describe — its SPP items are the
          entire risk, and a farm offer's total premium can never be $0, so the first item is collected
          HERE rather than after the offer exists. Without one the offer fails to rate outright. */}
      {farmRisk?.type === FarmRiskType.InlandMarine && (
        <Grid item xs={12}>
          {/* Grid container, not item: the parent's `spacing` rule outranks a single-class padding
              on an item. Same reason the offer route puts `coverageCard` on a container. */}
          <Grid container className={classes.inlandMarineContainer}>
            <InlandMarineForm
              fieldName={`${farmRiskPath}.coverages.scheduleCoverages`}
              riskType={FarmRiskType.InlandMarine}
              coverages={farmRisk?.coverages || {}}
              state={riskMaskState}
              hideCollapser
              validateParentOnAdd={false}
            />
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

InitialRiskDetail.propTypes = {
  missingDataCodes: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string]))
};

export default withDatePicker(InitialRiskDetail);
